import { useCallback, useEffect, useState } from "react";
import {
  Tag,
  Upload,
  Download,
  FileSpreadsheet,
  Trash2,
  Search,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Plus,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { adminApi, formatCOP, ADMIN_TOKEN_KEY } from "../../lib/api";
import { norm } from "../../lib/searchUtils";
import { toast } from "sonner";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("es-CO", {
      day: "2-digit", month: "short", year: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminPSM() {
  const [data, setData] = useState({ items: [], count: 0, active_count: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ nit: "", codigo_producto: "", precio: "", descuento_pct: "", vigente_hasta: "" });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/psm");
      setData(data || { items: [], count: 0, active_count: 0 });
    } catch {
      toast.error("No pudimos cargar PSM");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/psm");
        if (!cancelled) setData(data || { items: [], count: 0, active_count: 0 });
      } catch {
        if (!cancelled) toast.error("No pudimos cargar PSM");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/psm/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(data);
      const msg = `${data.applied} PSM aplicado${data.applied === 1 ? "" : "s"}.`
        + (data.skipped_no_product.length ? ` ${data.skipped_no_product.length} producto(s) no existen.` : "")
        + (data.skipped_no_client.length ? ` ${data.skipped_no_client.length} NIT(s) no existen.` : "");
      toast.success(msg);
      await fetchList();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const errs = detail?.parse_errors?.join(" · ") || detail || err.message;
      toast.error("Upload falló: " + errs);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDownloadTemplate = () => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY) || "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/psm/template`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "psm_plantilla_arcos.xlsx";
        a.click();
      })
      .catch(() => toast.error("No se pudo descargar la plantilla"));
  };

  const handleExport = () => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY) || "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/psm/export`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "psm_overrides.csv";
        a.click();
      })
      .catch(() => toast.error("No se pudo exportar"));
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar PSM ${item.codigo_producto} para NIT ${item.nit}?`)) return;
    try {
      await adminApi.delete(`/admin/psm`, {
        params: {
          nit: item.nit,
          codigo_producto: item.codigo_producto,
          vigente_desde: item.vigente_desde,
        },
      });
      toast.success("PSM eliminado");
      await fetchList();
    } catch (e) {
      toast.error("No se pudo eliminar: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleCreate = async () => {
    const precio = parseFloat(form.precio) || 0;
    const desc = parseFloat(form.descuento_pct) || 0;
    if (!form.nit || !form.codigo_producto) {
      toast.error("NIT y código son requeridos");
      return;
    }
    if (precio <= 0 && desc <= 0) {
      toast.error("Indicá Precio (>0) o Descuento % (>0)");
      return;
    }
    setCreating(true);
    try {
      await adminApi.post("/admin/psm", {
        nit: form.nit.trim(),
        codigo_producto: form.codigo_producto.trim(),
        precio: precio,
        descuento_pct: desc,
        vigente_hasta: form.vigente_hasta || null,
      });
      toast.success("PSM creado");
      setForm({ nit: "", codigo_producto: "", precio: "", descuento_pct: "", vigente_hasta: "" });
      setShowCreate(false);
      await fetchList();
    } catch (e) {
      toast.error("No se pudo crear: " + (e?.response?.data?.detail || e.message));
    } finally {
      setCreating(false);
    }
  };

  const q = norm(search.trim());
  const filteredItems = (data.items || []).filter((x) => {
    if (!q) return true;
    return (
      (x.nit || "").includes(q) ||
      norm(x.codigo_producto || "").includes(q) ||
      norm(x.descripcion || "").includes(q)
    );
  });

  return (
    <div data-testid="admin-psm-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">
            Override manual
          </p>
          <h1 className="font-serif text-3xl mt-1 tracking-tight flex items-center gap-3">
            <Tag className="h-7 w-7 text-cm-yellow" />
            Precios Especiales (PSM)
          </h1>
          <p className="text-sm text-cm-ink-soft mt-2 max-w-2xl">
            Sobrescribe el precio de catálogo para clientes específicos. Aplica por NIT (todas las sedes del mismo cliente).
            Sube un Excel con la plantilla o crea uno manualmente.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            className="border-cm-border"
            data-testid="download-template-btn"
          >
            <Download className="h-4 w-4 mr-2" /> Plantilla
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            className="border-cm-border"
            data-testid="export-btn"
            disabled={data.count === 0}
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Exportar CSV
          </Button>
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-cm-ink text-cm-cream rounded font-medium text-sm cursor-pointer hover:bg-cm-ink/90 transition-colors" data-testid="upload-label">
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Subir Excel
            <input
              type="file"
              accept=".xlsx,.xlsm"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading}
              data-testid="upload-input"
            />
          </label>
          <Button
            onClick={() => setShowCreate((v) => !v)}
            className="bg-cm-yellow text-cm-ink hover:bg-cm-yellow/90"
            data-testid="toggle-create-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Nuevo PSM
          </Button>
        </div>
      </div>

      {showCreate && (
        <Card className="mb-6 p-4 bg-cm-cream-alt border-cm-border shadow-none" data-testid="create-psm-card">
          <h3 className="text-sm font-semibold mb-3">Crear PSM manualmente</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
            <Input placeholder="NIT" value={form.nit} onChange={(e) => setForm({ ...form, nit: e.target.value })} data-testid="psm-form-nit" />
            <Input placeholder="Código producto" value={form.codigo_producto} onChange={(e) => setForm({ ...form, codigo_producto: e.target.value })} data-testid="psm-form-codigo" />
            <Input placeholder="Precio (COP)" type="number" value={form.precio} onChange={(e) => setForm({ ...form, precio: e.target.value })} data-testid="psm-form-precio" />
            <Input placeholder="Desc %" type="number" value={form.descuento_pct} onChange={(e) => setForm({ ...form, descuento_pct: e.target.value })} data-testid="psm-form-desc" />
            <Input placeholder="Vence (opcional)" type="date" value={form.vigente_hasta} onChange={(e) => setForm({ ...form, vigente_hasta: e.target.value })} data-testid="psm-form-vence" />
            <Button onClick={handleCreate} disabled={creating} className="bg-cm-ink text-cm-cream" data-testid="psm-form-submit">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear"}
            </Button>
          </div>
          <p className="text-[11px] text-cm-ink-soft mt-2">Llená <strong>Precio</strong> (precio fijo final con IVA) <strong>o</strong> <strong>Desc %</strong> (descuento sobre precio de lista). No es necesario llenar ambos.</p>
        </Card>
      )}

      {uploadResult && (
        <Card className="mb-6 p-4 bg-white border-cm-border shadow-none" data-testid="upload-result-card">
          <p className="text-sm font-semibold flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Resultado del upload
          </p>
          <ul className="text-xs text-cm-ink-soft mt-2 space-y-1">
            <li><strong className="text-cm-ink">{uploadResult.applied}</strong> PSM aplicado(s)</li>
            {uploadResult.skipped_no_product.length > 0 && (
              <li className="text-amber-700">
                <AlertCircle className="h-3 w-3 inline mr-1" />
                {uploadResult.skipped_no_product.length} producto(s) no existen: {uploadResult.skipped_no_product.slice(0, 3).join(", ")}{uploadResult.skipped_no_product.length > 3 ? "…" : ""}
              </li>
            )}
            {uploadResult.skipped_no_client.length > 0 && (
              <li className="text-amber-700">
                <AlertCircle className="h-3 w-3 inline mr-1" />
                {uploadResult.skipped_no_client.length} NIT(s) no encontrado(s): {uploadResult.skipped_no_client.slice(0, 3).join(", ")}{uploadResult.skipped_no_client.length > 3 ? "…" : ""}
              </li>
            )}
            {uploadResult.errors.length > 0 && (
              <li className="text-red-700">{uploadResult.errors.length} error(es): {uploadResult.errors[0]}</li>
            )}
          </ul>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <Card className="p-4 bg-white border-cm-border shadow-none">
          <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Total PSM</p>
          <p className="font-mono text-2xl font-bold mt-1">{data.count}</p>
        </Card>
        <Card className="p-4 bg-white border-cm-border shadow-none">
          <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Activos</p>
          <p className="font-mono text-2xl font-bold mt-1 text-emerald-700">{data.active_count}</p>
        </Card>
        <Card className="p-4 bg-white border-cm-border shadow-none">
          <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Vencidos</p>
          <p className="font-mono text-2xl font-bold mt-1 text-cm-ink-soft/60">
            {data.count - data.active_count}
          </p>
        </Card>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-cm-ink-soft" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por NIT, código o descripción…"
          className="pl-9 bg-white border-cm-border"
          data-testid="search-input"
        />
      </div>

      {loading ? (
        <Card className="p-12 text-center bg-white border-cm-border shadow-none">
          <Loader2 className="h-8 w-8 mx-auto animate-spin text-cm-ink-soft" />
          <p className="text-sm text-cm-ink-soft mt-3">Cargando PSM…</p>
        </Card>
      ) : filteredItems.length === 0 ? (
        <Card className="p-12 text-center bg-white border-cm-border shadow-none" data-testid="psm-empty">
          <Tag className="h-12 w-12 mx-auto text-cm-ink-soft/40" />
          <h2 className="font-serif text-xl mt-4">No hay PSM configurados</h2>
          <p className="text-sm text-cm-ink-soft mt-2 max-w-md mx-auto">
            Descarga la plantilla, llénala con NITs y códigos de producto, y súbela. O crea uno manualmente.
          </p>
        </Card>
      ) : (
        <Card className="bg-white border-cm-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="psm-table">
              <thead className="bg-cm-cream-alt border-b border-cm-border">
                <tr className="text-left text-[10px] uppercase tracking-widest text-cm-ink-soft">
                  <th className="px-4 py-3">NIT</th>
                  <th className="px-4 py-3">Producto</th>
                  <th className="px-4 py-3 text-right">Precio especial</th>
                  <th className="px-4 py-3 text-right">Desc %</th>
                  <th className="px-4 py-3">Vigencia</th>
                  <th className="px-4 py-3 text-center">Estado</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cm-border">
                {filteredItems.map((p) => (
                  <tr
                    key={`${p.nit}-${p.codigo_producto}-${p.vigente_desde}`}
                    className="hover:bg-cm-cream-alt/30"
                    data-testid={`psm-row-${p.nit}-${p.codigo_producto}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs">{p.nit}</td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs">{p.codigo_producto}</div>
                      <div className="text-xs text-cm-ink-soft">{p.descripcion || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">
                      {p.precio > 0 ? formatCOP(p.precio) : <span className="text-cm-ink-soft">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {p.descuento_pct > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold text-xs">
                          {p.descuento_pct.toFixed(2)}%
                        </span>
                      ) : <span className="text-cm-ink-soft">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-cm-ink-soft">
                      {formatDate(p.vigente_desde)}
                      {p.vigente_hasta && ` → ${formatDate(p.vigente_hasta)}`}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {p.activo ? (
                        <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-widest">
                          Activo
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold text-cm-ink-soft/60 uppercase tracking-widest">
                          Vencido
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(p)}
                        className="text-red-600 hover:text-red-800"
                        title="Eliminar"
                        data-testid={`psm-delete-${p.nit}-${p.codigo_producto}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="mt-6 p-4 bg-cm-cream-alt border border-cm-border rounded-md text-xs text-cm-ink-soft leading-relaxed">
        <p className="font-semibold text-cm-ink mb-1 flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5" /> Plantilla de Excel (estilo Arcos)
        </p>
        <p className="mb-2">
          Columnas obligatorias: <span className="font-mono">NIT, Código, Desde</span> y al menos <strong>una de</strong> <span className="font-mono">Precio</span> o <span className="font-mono">Desc</span> (descuento %). Columnas opcionales: <span className="font-mono">Concepto, Hasta</span>.
        </p>
        <p className="mb-2">
          La plantilla mantiene el orden visual de Arcos (PrecioEspecialId, Cod.Cliente, Cliente, NIT, Código, Concepto, Precio, Desc, Desde, Hasta) — las columnas <span className="font-mono">PrecioEspecialId</span> y <span className="font-mono">Cod.Cliente</span> se ignoran al parsear (solo facilitan el copy/paste desde Arcos).
        </p>
        <p>
          Si tu fila tiene <strong>Precio</strong>: se usa como precio fijo final (con IVA). Si tiene <strong>Desc</strong>: se calcula <span className="font-mono">precio_final = precio_lista × (1 − Desc/100)</span> al momento de mostrar el catálogo al cliente. El sistema valida NIT y Código contra Arcos; filas con errores no se aplican (te las mostramos en el resumen).
        </p>
      </div>
    </div>
  );
}
